import { useState } from 'react';
import { toast } from 'react-toastify';
import { TwitterTweetEmbed } from 'react-twitter-embed';
import { Post } from '@sodamnfoolish/sjc-backend-types/src/api/feed/dto/post';
import { MediaSource } from '@sodamnfoolish/sjc-backend-types/src/api/feed/media-source';
import { ExternalMedia, InternalMedia } from '@sodamnfoolish/sjc-backend-types/src/api/feed/dto';
import { Metadata } from '@sodamnfoolish/sjc-backend-types/src/api/token/dto';
import MessageCard from '../../MessageCard';
import { getTwitterIdFromUrl } from '../../../utils/ge-tweet-id-from-url';
import { useBackend } from '../../../Hooks/UseBackend';

import { ImageSource } from './types';

const blobToFile = (blob: Blob, filename: string): File => {
    return new File([blob], filename, { type: blob.type });
};
const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject('Error converting blob to Base64');
        reader.readAsDataURL(blob);
    });
};

export function Card({
    image,
    url,
    post,
    background = '',
    platform = '',
    onImageChange,
    onMetadataChange,
}: {
    image: string;
    url?: string;
    post?: Post;
    background?: string;
    platform?: string;
    onMetadataChange: (metadata: Partial<Metadata>) => void;
} & ImageSource) {
    const { getMedia, generateTokenMetadataByTweetUrl, generateTokenMetadataByUrl, getTokenMetadata } = useBackend();

    const [solanaTokenToParse, setSolanaTokenToParse] = useState<string>('');
    const [tweetUrlToParse, setTweetUrlToParse] = useState<string>('');
    const [webUrlToParse, setWebUrlToParse] = useState<string>('');

    const imageClick = async (index: number) => {
        if (post && post.media && post.media.length > 0) {
            const mediaItem = post.media[index];

            try {
                if (mediaItem.source === MediaSource.Internal) {
                    const blob = await getMedia((mediaItem as InternalMedia).id);
                    const base64Image = await blobToBase64(blob);

                    onImageChange(base64Image, blobToFile(blob, 'postImage.jpg'));
                } else if (mediaItem.source === MediaSource.External) {
                    const externalUrl = (mediaItem as ExternalMedia).url;
                    const response = await fetch(externalUrl);
                    const blob = await response.blob();
                    const base64Image = await blobToBase64(blob);

                    onImageChange(base64Image, blobToFile(blob, 'postImage.jpg'));
                }
            } catch (error) {
                console.error('Error fetching media:', error);
            }
        }
    };

    async function parseTokenMetadata(mint: string) {
        const metadata = await getTokenMetadata(mint);

        if (!metadata) {
            toast.error('No metadata found');
            return;
        }

        onMetadataChange(metadata);
    }

    async function generateMetadataByTweetUrl(url: string) {
        if (!url) {
            return toast.error('URL not provided');
        }
        toast.info('Generating metadata from tweet URL...');
        const metadata = await generateTokenMetadataByTweetUrl(url);

        onMetadataChange({
            name: metadata.name,
            symbol: metadata.symbol,
            description: url,
            twitter: url,
        });

        toast.success('Metadata generated successfully!');
    }

    async function generateMetadataByUrl(url: string) {
        if (!url) {
            return toast.error('URL not provided');
        }
        toast.info('Generating metadata from web URL...');
        const metadata = await generateTokenMetadataByUrl(url);

        onMetadataChange({
            name: metadata.name,
            symbol: metadata.symbol,
            description: url,
            twitter: url,
        });

        toast.success('Metadata generated successfully!');
    }

    return (
        <section className='card'>
            <div className='mes-card-form'>
                {post ? (
                    <>
                        <div className='header-card'>
                            <div className='icon-naming-link'>
                                <img className='icon' src={`data:image/jpeg;base64,${image}`} alt='Icon' />
                                <div className='name-url'>
                                    {post ? (
                                        <>
                                            <h3>{post.username}</h3>
                                            <a
                                                className='link-to-channel'
                                                href={url ? url : '#'}
                                                target='_blank'
                                                rel='noreferrer'
                                                onClick={(e) => {
                                                    if (!url) {
                                                        e.preventDefault();
                                                        console.log('URL not provided');
                                                    }
                                                }}
                                            >
                                                <h3>{url && url.includes('https://') ? url.slice(8) : url}</h3>
                                            </a>
                                        </>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                        <MessageCard post={post} background={background ?? ''} onImageClick={imageClick} platform={platform ?? ''} username={post.username} />
                    </>
                ) : (
                    <div className='no-post-side-panel'>
                        <div className='side-panel-case'>
                            <h3>Parse Existing Solana Token</h3>
                            <div className='side-panel-inp-btn-case'>
                                <input
                                    type='text'
                                    value={solanaTokenToParse}
                                    onChange={(e) => {
                                        setSolanaTokenToParse(e.target.value);
                                    }}
                                />
                                <button
                                    className='pic-btn'
                                    onClick={() => {
                                        parseTokenMetadata(solanaTokenToParse);
                                    }}
                                >
                                    Parse
                                </button>
                            </div>
                        </div>
                        <div className='side-panel-case'>
                            <h3>Ask AI To Generate From Tweet URL</h3>
                            <div className='side-panel-inp-btn-case'>
                                <input
                                    type='text'
                                    value={tweetUrlToParse}
                                    onChange={(e) => {
                                        setTweetUrlToParse(e.target.value);
                                    }}
                                />
                                <button
                                    className='pic-btn'
                                    onClick={() => {
                                        generateMetadataByTweetUrl(tweetUrlToParse);
                                    }}
                                >
                                    Start
                                </button>
                            </div>
                        </div>
                        <div className='side-panel-case'>
                            <h3>Ask AI To Generate From Web URL</h3>
                            <div className='side-panel-inp-btn-case'>
                                <input
                                    type='text'
                                    value={webUrlToParse}
                                    onChange={(e) => {
                                        setWebUrlToParse(e.target.value);
                                    }}
                                />
                                <button
                                    className='pic-btn'
                                    onClick={() => {
                                        generateMetadataByUrl(webUrlToParse);
                                    }}
                                >
                                    Start
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className='tweetForm'>
                {post && (post.url.includes('twitter.com') || post.url.includes('x.com')) ? (
                    <div data-theme='dark' style={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
                        <div
                            className='display-tweet-form'
                            data-theme='dark'
                            style={{
                                position: 'relative',
                                width: '100%',

                                overflow: 'hidden',
                                zIndex: 2,
                            }}
                        >
                            <TwitterTweetEmbed tweetId={getTwitterIdFromUrl(post.url)} options={{ theme: 'dark' }} />
                        </div>
                    </div>
                ) : (
                    <p></p>
                )}
            </div>
            <div className='ocr-case'>
                {post && (post.url.includes('twitter.com') || post.url.includes('x.com')) ? (
                    <div className='ocr-text'>
                        <div>
                            <h3 className='ocr-headline'>OCR:</h3>
                        </div>
                        <div>
                            <h3 className='ocr-main-text'>Example 1234556</h3>
                        </div>
                    </div>
                ) : (
                    <p></p>
                )}
            </div>
        </section>
    );
}
